<template>
  <div class="work-instruction-detail">
    <!-- Header -->
    <div class="header-section">
      <div class="header-top">
        <div class="title-group">
          <h1>{{ workInstruction.instruction_number || '새 생산지시서' }}</h1>
          <el-tag
            v-if="workInstruction.status"
            :type="getStatusType(workInstruction.status)"
            size="large"
            class="status-tag"
          >
            {{ getStatusLabel(workInstruction.status) }}
          </el-tag>
          <el-tag
            v-if="workInstruction.approval_status === 'PENDING'"
            type="warning"
            size="large"
            class="status-tag"
          >
            결재 진행중
          </el-tag>
        </div>
        <div class="action-buttons">
          <el-button
            v-if="!isEditing && workInstruction.status === 'DRAFT' && workInstruction.approval_status !== 'PENDING'"
            type="primary"
            plain
            @click="openApprovalDialog"
          >
            결재 상신
          </el-button>
          <el-button
            v-if="!isEditing"
            type="primary"
            @click="enterEditMode"
            :loading="loading"
          >
            <el-icon><Edit /></el-icon>
            수정
          </el-button>
          <!-- 출력 = PDF 단일 (2026-07-30 사이먼: 엑셀 출력·인쇄 버튼 제거,
               PDF만 유지 — 발행본과 동일 서식 하나로 통일) -->
          <el-dropdown v-if="!isEditing" @command="downloadInstructionPdf" style="margin: 0 10px">
            <el-button :loading="loading">
              <el-icon><Document /></el-icon>
              PDF 출력<el-icon class="el-icon--right"><ArrowDown /></el-icon>
            </el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="A4">A4 — 내부 검토용</el-dropdown-item>
                <el-dropdown-item command="A3">A3 — 공장 발송용 (스와치 부착란)</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
          <el-button
            v-if="!isEditing"
            type="warning"
            @click="openIssueDialog"
            :loading="loading"
          >
            <el-icon><Promotion /></el-icon>
            공장 발행
          </el-button>
          <el-button
            v-if="isEditing"
            type="success"
            @click="handleSave"
            :loading="loading"
          >
            <el-icon><Check /></el-icon>
            저장
          </el-button>
          <el-button
            v-if="isEditing"
            @click="cancelEdit"
            :loading="loading"
          >
            <el-icon><Close /></el-icon>
            취소
          </el-button>
        </div>
      </div>

      <!-- MR↔WI 불일치 경고 (2026-08-01) — 메인오더 수량·자재가 바뀐 뒤
           동기화하지 않으면 낡은 문서가 공장으로 발행될 수 있음 -->
      <el-alert
        v-if="mrDiff && mrDiff.linked && !mrDiff.match"
        type="warning"
        :closable="false"
        show-icon
        class="mr-diff-alert"
      >
        <template #title>
          메인오더와 불일치 — {{ mrDiffSummary }}
          <el-button type="warning" size="small" :loading="syncing" @click="syncFromMainRequest" style="margin-left: 12px">
            메인오더 기준으로 동기화
          </el-button>
        </template>
      </el-alert>

      <!-- Summary Cards -->
      <!-- 임베드 모드(workInstructionId prop)면 숨김 — 상위 메인오더 배너와 전부 중복 (2026-08-01 세로 압축). 단독 화면에선 유지 -->
      <div v-if="!workInstructionId" class="summary-cards">
        <el-card shadow="hover" class="summary-card">
          <template #header>
            <div class="card-header">
              <span>메인요청</span>
            </div>
          </template>
          <el-link
            v-if="workInstruction.main_request"
            type="primary"
            :href="`/main-requests/${workInstruction.main_request}`"
          >
            {{ mainRequestNumber }}
          </el-link>
          <span v-else class="text-muted">-</span>
        </el-card>

        <el-card shadow="hover" class="summary-card">
          <template #header>
            <div class="card-header">
              <span>설계/브랜드</span>
            </div>
          </template>
          <div class="summary-content">
            <div v-if="workInstruction.design" class="info-row">
              <span class="label">디자인:</span>
              <span class="value">{{ designInfo }}</span>
            </div>
            <div v-if="workInstruction.brand" class="info-row">
              <span class="label">브랜드:</span>
              <span class="value">{{ brandInfo }}</span>
            </div>
          </div>
        </el-card>

        <el-card shadow="hover" class="summary-card">
          <template #header>
            <div class="card-header">
              <span>생산정보</span>
            </div>
          </template>
          <div class="summary-content">
            <div v-if="workInstruction.manufacturer" class="info-row">
              <span class="label">공장:</span>
              <span class="value">{{ manufacturerInfo }}</span>
            </div>
            <div class="info-row">
              <span class="label">총수량:</span>
              <span class="value font-bold">{{ workInstruction.total_quantity || 0 }}</span>
            </div>
          </div>
        </el-card>
      </div>
    </div>

    <!-- Basic Info Section -->
    <el-card class="basic-info-card">
      <template #header>
        <div class="card-header">
          <span>기본정보</span>
        </div>
      </template>

      <el-form label-width="120px">
        <!-- Row 1: MixRatio, ProcessMethod -->
        <el-row :gutter="20">
          <el-col :xs="24" :sm="12">
            <el-form-item label="혼용율">
              <el-input
                v-if="isEditing"
                v-model="formData.mix_ratio"
                placeholder="예: Cotton 50%, Poly 50%"
              />
              <span v-else class="text-muted">
                {{ workInstruction.mix_ratio || '-' }}
              </span>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12">
            <el-form-item label="가공방법">
              <el-input
                v-if="isEditing"
                v-model="formData.process_method"
                placeholder="예: 수동 봉제"
              />
              <span v-else class="text-muted">
                {{ workInstruction.process_method || '-' }}
              </span>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Row 1.5: 생산처 / 출고일자 / 패킹방법 (2026-07-29 fit-gap ①) -->
        <el-row :gutter="20">
          <el-col :xs="24" :sm="8">
            <el-form-item label="생산처">
              <el-select
                v-if="isEditing"
                v-model="formData.manufacturer"
                filterable
                clearable
                placeholder="생산공장 선택"
                style="width: 100%"
              >
                <el-option
                  v-for="mf in manufacturers"
                  :key="mf.id"
                  :label="mf.name"
                  :value="mf.id"
                />
              </el-select>
              <span v-else class="text-muted">
                {{ workInstruction.manufacturer_name || '-' }}
              </span>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="8">
            <el-form-item label="출고일자">
              <el-date-picker
                v-if="isEditing"
                v-model="formData.shipment_date"
                type="date"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                placeholder="공장 출고 예정일"
                style="width: 100%"
              />
              <span v-else class="text-muted">
                {{ workInstruction.shipment_date || '-' }}
              </span>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="8">
            <el-form-item label="패킹방법">
              <el-input
                v-if="isEditing"
                v-model="formData.packing_method"
                placeholder="예: 옷걸이 + 폴리백 + 수트케이스"
              />
              <span v-else class="text-muted">
                {{ workInstruction.packing_method || '-' }}
              </span>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Row 1.7: 상품코드 / 기준사이즈 (2026-07-29 종이 작지 1:1) -->
        <el-row :gutter="20">
          <el-col :xs="24" :sm="12">
            <el-form-item label="상품코드">
              <el-input
                v-if="isEditing"
                v-model="formData.product_code"
                placeholder="리테일러 상품코드 (확정 후 기입)"
              />
              <span v-else class="text-muted">
                {{ workInstruction.product_code || '-' }}
              </span>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12">
            <el-form-item label="기준 사이즈">
              <el-input
                v-if="isEditing"
                v-model="formData.base_size"
                placeholder="예: 55 — 지시서에 ◯ 표시"
                style="width: 160px"
              />
              <span v-else class="text-muted">
                {{ workInstruction.base_size || '-' }}
              </span>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 주의사항 (2026-07-29 fit-gap ①) -->
        <el-form-item label="주의사항">
          <el-input
            v-if="isEditing"
            v-model="formData.precautions"
            type="textarea"
            :rows="4"
            placeholder="한 줄에 한 항목씩 — 출력 시 번호가 자동 부여됩니다 (예: 린넨원단 특성상 올 빠짐 주의)"
          />
          <ol v-else-if="precautionLines.length" class="precaution-list">
            <li v-for="(line, idx) in precautionLines" :key="idx">{{ line }}</li>
          </ol>
          <span v-else class="text-muted">-</span>
        </el-form-item>

        <!-- Row 2: Total Quantity, Image -->
        <el-row :gutter="20">
          <el-col :xs="24" :sm="12">
            <el-form-item label="총수량">
              <el-input-number
                v-if="isEditing"
                v-model="formData.total_quantity"
                :min="0"
                style="width: 100%"
              />
              <span v-else class="text-muted">
                {{ workInstruction.total_quantity || 0 }}
              </span>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12">
            <el-form-item label="스케치">
              <!-- 다중 스케치 (2026-08-01 사이먼 1안) — 부위별 주석은 디자이너
                   도구에서 완성, 여기엔 완성본을 앞/뒤/디테일 여러 장 업로드.
                   PDF 스케치 영역에 원본 화질로 크게 실림. 업로드 즉시 저장 -->
              <div class="sketch-gallery">
                <figure v-for="im in wiImages" :key="im.id" class="sk-item">
                  <el-image :src="im.image" fit="contain" :preview-src-list="wiImages.map(i => i.image)" class="sk-thumb" />
                  <figcaption>
                    {{ im.caption || '-' }}
                    <el-button v-if="isEditing" link type="danger" size="small" @click="deleteWiImage(im)">삭제</el-button>
                  </figcaption>
                </figure>
                <div v-if="isEditing" class="sk-uploader">
                  <el-input v-model="newImageCaption" placeholder="설명 (예: 앞판)" size="small" style="width: 110px" />
                  <el-upload :auto-upload="false" action="#" accept="image/*" :show-file-list="false" @change="uploadWiImage">
                    <el-button size="small" type="primary" plain>+ 이미지 추가</el-button>
                  </el-upload>
                </div>
                <span v-if="!wiImages.length && !isEditing" class="text-muted">-</span>
              </div>
              <!-- 구버전 단일 이미지 (다중 이미지 없을 때만 표시) -->
              <div v-if="!wiImages.length && workInstruction.image" class="image-preview">
                <el-image :src="workInstruction.image" fit="cover" style="max-width: 200px" />
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Memo -->
        <el-form-item label="메모">
          <el-input
            v-if="isEditing"
            v-model="formData.memo"
            type="textarea"
            :rows="3"
            placeholder="작업지시 관련 메모"
          />
          <el-text v-else type="info">
            {{ workInstruction.memo || '-' }}
          </el-text>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- Tabs Section -->
    <el-card class="tabs-card">
      <el-tabs v-model="activeTab" type="border-card">
        <!-- Tab 1: 규격 (Specs) -->
        <el-tab-pane label="규격" name="specs">
          <div class="tab-content">
            <div class="tab-header">
              <h3>부위별 규격</h3>
              <el-button
                v-if="isEditing"
                type="primary"
                size="small"
                @click="addNewSpec"
              >
                <el-icon><Plus /></el-icon>
                규격 추가
              </el-button>
            </div>

            <el-table
              :data="specs"
              stripe
              style="width: 100%"
              :default-sort="{ prop: 'part', order: 'ascending' }"
            >
              <el-table-column prop="part" label="부위" width="110">
                <template #default="{ row }">
                  <el-input
                    v-if="isEditing"
                    v-model="row.part"
                    placeholder="예: 어깨"
                    size="small"
                  />
                  <span v-else>{{ row.part }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="part_en" label="부위(영문)" width="130">
                <template #default="{ row }">
                  <el-input
                    v-if="isEditing"
                    v-model="row.part_en"
                    placeholder="예: SHOULDER"
                    size="small"
                  />
                  <span v-else>{{ row.part_en || '-' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="size" label="사이즈" width="90">
                <template #default="{ row }">
                  <el-input
                    v-if="isEditing"
                    v-model="row.size"
                    placeholder="예: 55"
                    size="small"
                  />
                  <span v-else>{{ row.size }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="measurement" label="지시치수" width="100">
                <template #default="{ row }">
                  <el-input
                    v-if="isEditing"
                    v-model="row.measurement"
                    placeholder="예: 42"
                    size="small"
                  />
                  <span v-else>{{ row.measurement }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="qc_measurement" label="QC측정" width="100">
                <template #default="{ row }">
                  <el-input
                    v-if="isEditing"
                    v-model="row.qc_measurement"
                    placeholder="실측"
                    size="small"
                  />
                  <span v-else>{{ row.qc_measurement || '-' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="pattern_measurement" label="패턴치수" width="100">
                <template #default="{ row }">
                  <el-input
                    v-if="isEditing"
                    v-model="row.pattern_measurement"
                    placeholder="패턴"
                    size="small"
                  />
                  <span v-else>{{ row.pattern_measurement || '-' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="fpp" label="FPP" width="80">
                <template #default="{ row }">
                  <el-input
                    v-if="isEditing"
                    v-model="row.fpp"
                    placeholder="선택"
                    size="small"
                  />
                  <span v-else>{{ row.fpp || '-' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="tolerance" label="허용편차" width="90">
                <template #default="{ row }">
                  <el-input
                    v-if="isEditing"
                    v-model="row.tolerance"
                    placeholder="예: 0.5"
                    size="small"
                  />
                  <span v-else>{{ row.tolerance || '-' }}</span>
                </template>
              </el-table-column>
              <el-table-column v-if="isEditing" label="작업" width="80" align="center">
                <template #default="{ $index }">
                  <el-button
                    text
                    type="danger"
                    size="small"
                    @click="removeSpec($index)"
                  >
                    <el-icon><Delete /></el-icon>
                  </el-button>
                </template>
              </el-table-column>
            </el-table>

            <div v-if="specs.length === 0" class="empty-state">
              <el-empty description="규격 정보 없음" />
            </div>
          </div>
        </el-tab-pane>

        <!-- Tab 2: 수량배분 (Quantities) -->
        <el-tab-pane label="수량배분" name="quantities">
          <div class="tab-content">
            <div class="tab-header">
              <h3>색상/사이즈별 수량</h3>
              <el-button
                v-if="isEditing"
                type="primary"
                size="small"
                @click="addNewQuantity"
              >
                <el-icon><Plus /></el-icon>
                수량 추가
              </el-button>
            </div>

            <!-- Pivot Table Display -->
            <div class="pivot-container">
              <el-table
                :data="quantityTableData"
                stripe
                border
                style="width: 100%"
              >
                <el-table-column prop="size" label="사이즈" width="100" align="center" />
                <el-table-column
                  v-for="color in uniqueColors"
                  :key="`color-${color}`"
                  :label="color"
                  align="center"
                  min-width="100"
                >
                  <template #default="{ row }">
                    <el-input-number
                      v-if="isEditing"
                      v-model="row[color]"
                      :min="0"
                      size="small"
                      @change="updateQuantityTotal"
                    />
                    <span v-else class="font-bold">
                      {{ row[color] || '-' }}<span v-if="spareAt(color, row.size)" class="spare-plus">+{{ spareAt(color, row.size) }}</span>
                    </span>
                  </template>
                </el-table-column>
                <el-table-column label="합계" width="80" align="center" prop="total" />
              </el-table>
            </div>

            <!-- List View for Editing -->
            <el-table
              v-if="isEditing"
              :data="quantities"
              stripe
              style="width: 100%; margin-top: 20px"
            >
              <el-table-column prop="color" label="색상" width="120">
                <template #default="{ row }">
                  <el-input
                    v-model="row.color"
                    placeholder="색상명"
                    size="small"
                  />
                </template>
              </el-table-column>
              <el-table-column prop="size" label="사이즈" width="100">
                <template #default="{ row }">
                  <el-input
                    v-model="row.size"
                    placeholder="사이즈"
                    size="small"
                  />
                </template>
              </el-table-column>
              <el-table-column prop="quantity" label="수량" width="100">
                <template #default="{ row }">
                  <el-input-number
                    v-model="row.quantity"
                    :min="0"
                    size="small"
                  />
                </template>
              </el-table-column>
              <el-table-column prop="spare_quantity" label="여유분" width="100">
                <template #default="{ row }">
                  <el-input-number
                    v-model="row.spare_quantity"
                    :min="0"
                    size="small"
                  />
                </template>
              </el-table-column>
              <el-table-column label="작업" width="80" align="center">
                <template #default="{ $index }">
                  <el-button
                    text
                    type="danger"
                    size="small"
                    @click="removeQuantity($index)"
                  >
                    <el-icon><Delete /></el-icon>
                  </el-button>
                </template>
              </el-table-column>
            </el-table>

            <div v-if="quantities.length === 0" class="empty-state">
              <el-empty description="수량 정보 없음" />
            </div>
          </div>
        </el-tab-pane>

        <!-- Tab 3: 자재 (Materials) -->
        <el-tab-pane label="자재" name="materials">
          <div class="tab-content">
            <div class="tab-header">
              <h3>필요 자재 목록</h3>
              <div>
                <el-button
                  v-if="isEditing"
                  size="small"
                  :loading="importingMaterials"
                  @click="importMaterialsFromMainRequest"
                >
                  메인오더에서 가져오기 (자재+수량)
                </el-button>
                <el-button
                  v-if="isEditing"
                  type="primary"
                  size="small"
                  @click="addNewMaterial"
                >
                  <el-icon><Plus /></el-icon>
                  자재 추가
                </el-button>
              </div>
            </div>

            <el-table
              :data="materials"
              stripe
              style="width: 100%"
            >
              <el-table-column prop="category" label="구분" width="100">
                <template #default="{ row }">
                  <el-select
                    v-if="isEditing"
                    v-model="row.category"
                    size="small"
                    placeholder="구분"
                  >
                    <el-option
                      v-for="c in categoryOptions"
                      :key="c.value"
                      :label="c.label"
                      :value="c.value"
                    />
                  </el-select>
                  <span v-else>{{ row.category_display || '기타' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="material_type" label="자재구분" width="120">
                <template #default="{ row }">
                  <el-select
                    v-if="isEditing"
                    v-model="row.material_type"
                    size="small"
                    placeholder="구분 선택"
                  >
                    <el-option label="원자재" value="RAW" />
                    <el-option label="부자재" value="ACCESSORY" />
                  </el-select>
                  <el-tag v-else :type="getMaterialTypeTag(row.material_type)">
                    {{ getMaterialTypeLabel(row.material_type) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="material_name" label="자재명" min-width="150">
                <template #default="{ row }">
                  <el-input
                    v-if="isEditing"
                    v-model="row.material_name"
                    placeholder="자재명"
                    size="small"
                  />
                  <span v-else>{{ row.material_name }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="usage_part" label="사용부위" width="110">
                <!-- 2026-07-30 (사이먼) — 부위 표기가 용도/비고 자유 텍스트에
                     뭉뚱그려지던 것을 명시 필드화. 지시서 자재표에 별도 열 출력 -->
                <template #default="{ row }">
                  <el-input
                    v-if="isEditing"
                    v-model="row.usage_part"
                    placeholder="예: 에리, 구찌"
                    size="small"
                  />
                  <span v-else>{{ row.usage_part || '-' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="specification" label="규격" width="110">
                <template #default="{ row }">
                  <el-input
                    v-if="isEditing"
                    v-model="row.specification"
                    placeholder="규격"
                    size="small"
                  />
                  <span v-else>{{ row.specification || '-' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="color" label="색상/도금" width="120">
                <template #default="{ row }">
                  <el-input
                    v-if="isEditing"
                    v-model="row.color"
                    placeholder="예: 흑니켈"
                    size="small"
                  />
                  <span v-else>{{ row.color || '-' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="required_quantity" label="소요량" width="100">
                <template #default="{ row }">
                  <el-input-number
                    v-if="isEditing"
                    v-model="row.required_quantity"
                    :min="0"
                    :precision="3"
                    size="small"
                  />
                  <span v-else>{{ row.required_quantity }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="remarks" label="용도/비고" min-width="110">
                <template #default="{ row }">
                  <el-input
                    v-if="isEditing"
                    v-model="row.remarks"
                    placeholder="예: 허리1줄"
                    size="small"
                  />
                  <span v-else>{{ row.remarks || '-' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="supplier" label="거래처" min-width="130">
                <template #default="{ row }">
                  <!-- FK 드롭다운 (2026-07-29) — 종전 자유 텍스트는 FK와 불일치라
                       저장이 조용히 실패하던 잠재 버그. :value는 supplier_id -->
                  <el-select
                    v-if="isEditing"
                    v-model="row.supplier"
                    filterable
                    clearable
                    placeholder="거래처"
                    size="small"
                    style="width: 100%"
                  >
                    <el-option
                      v-for="sp in suppliers"
                      :key="sp.supplier_id"
                      :label="sp.name"
                      :value="sp.supplier_id"
                    />
                  </el-select>
                  <span v-else>{{ row.supplier_name || '-' }}</span>
                </template>
              </el-table-column>
              <el-table-column v-if="isEditing" label="작업" width="80" align="center">
                <template #default="{ $index }">
                  <el-button
                    text
                    type="danger"
                    size="small"
                    @click="removeMaterial($index)"
                  >
                    <el-icon><Delete /></el-icon>
                  </el-button>
                </template>
              </el-table-column>
            </el-table>

            <div v-if="materials.length === 0" class="empty-state">
              <el-empty description="자재 정보 없음" />
            </div>
          </div>
        </el-tab-pane>

        <!-- Tab 4: 수정이력 (Revisions, 2026-07-17) -->
        <el-tab-pane label="수정이력" name="revisions">
          <div class="tab-content">
            <div class="tab-header">
              <h3>날짜별 수정지시 (가봉/직봉/QC)</h3>
              <el-button type="primary" size="small" @click="openRevisionDialog()">
                <el-icon><Plus /></el-icon>
                수정지시 추가
              </el-button>
            </div>

            <el-timeline v-if="revisions.length">
              <el-timeline-item
                v-for="rev in revisions"
                :key="rev.id"
                :timestamp="rev.revision_date + (rev.title ? ` · ${rev.title}` : '')"
                placement="top"
              >
                <el-card shadow="never" class="revision-card">
                  <ol class="revision-list">
                    <li v-for="(line, idx) in revisionLines(rev)" :key="idx">{{ line }}</li>
                  </ol>
                  <div class="revision-actions">
                    <el-button size="small" text type="primary" @click="openRevisionDialog(rev)">수정</el-button>
                    <el-button size="small" text type="danger" @click="deleteRevision(rev)">삭제</el-button>
                  </div>
                </el-card>
              </el-timeline-item>
            </el-timeline>
            <div v-else class="empty-state">
              <el-empty description="수정지시 이력 없음" />
            </div>
          </div>
        </el-tab-pane>

        <!-- Tab 5: 발행 이력 (Versions, 2026-07-29) -->
        <el-tab-pane name="versions">
          <template #label>
            발행 이력
            <el-badge v-if="versions.length" :value="versions.length" type="primary" class="version-badge" />
          </template>
          <div class="tab-content">
            <div class="tab-header">
              <h3>공장 배포본 히스토리 — 발행 당시 파일 그대로 보존</h3>
            </div>
            <el-table :data="versions" stripe style="width: 100%">
              <el-table-column label="버전" width="80" align="center">
                <template #default="{ row }">
                  <el-tag size="small">Rev.{{ row.version_no }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="created_at" label="발행일시" width="160">
                <template #default="{ row }">
                  {{ (row.created_at || '').replace('T', ' ').slice(0, 16) }}
                </template>
              </el-table-column>
              <el-table-column prop="issued_by_name" label="발행자" width="100" />
              <el-table-column prop="paper_size" label="용지" width="70" align="center" />
              <el-table-column prop="note" label="변경 요지" min-width="200">
                <template #default="{ row }">{{ row.note || '-' }}</template>
              </el-table-column>
              <el-table-column label="공장 접수" width="150" align="center">
                <template #default="{ row }">
                  <template v-if="row.acknowledged_at">
                    <el-tag size="small" type="success">접수됨</el-tag>
                    <div class="ack-info">
                      {{ row.acknowledged_by }} · {{ (row.acknowledged_at || '').slice(0, 10) }}
                    </div>
                  </template>
                  <el-button v-else size="small" text @click="acknowledgeVersion(row)">
                    접수확인
                  </el-button>
                </template>
              </el-table-column>
              <el-table-column label="발행본" width="110" align="center">
                <template #default="{ row }">
                  <el-button size="small" text type="primary" @click="downloadVersion(row)">
                    <el-icon><Download /></el-icon> 다운로드
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
            <div v-if="versions.length === 0" class="empty-state">
              <el-empty description="아직 발행 이력 없음 — [공장 발행] 버튼으로 첫 버전을 만드세요" />
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <!-- 공장 발행 다이얼로그 (2026-07-29) -->
    <el-dialog v-model="showIssueDialog" title="공장 발행 — 버전 스냅샷" width="480px">
      <el-alert
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 14px"
        title="현재 지시서 내용이 Rev 버전 PDF로 고정 보존됩니다. 이후 수정해도 이 발행본은 변하지 않습니다."
      />
      <el-form label-width="90px">
        <el-form-item label="용지">
          <el-radio-group v-model="issueForm.paper">
            <el-radio-button label="A4">A4 — 내부용</el-radio-button>
            <el-radio-button label="A3">A3 — 공장 발송 (스와치 부착란)</el-radio-button>
          </el-radio-group>
          <div class="paper-hint">공장 발송본은 A3로 출력해 원단 스와치를 부착란에 붙여 보냅니다.</div>
        </el-form-item>
        <el-form-item label="변경 요지">
          <el-input
            v-model="issueForm.note"
            type="textarea"
            :rows="3"
            placeholder="이번 발행에서 달라진 점 (예: 밑단 치수 수정, 부자재 업체 변경)"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showIssueDialog = false">취소</el-button>
        <el-button type="warning" :loading="issuing" @click="submitIssue">
          Rev.{{ nextVersionNo }} 발행
        </el-button>
      </template>
    </el-dialog>

    <!-- 결재선 지정 상신 (2026-07-29) — 발주서와 동일 다이얼로그 -->
    <ApprovalSubmitDialog
      v-model="showApprovalDialog"
      document-type="work_instruction"
      :document-id="wiId()"
      document-api-path="production/work-instructions"
      @submitted="onApprovalSubmitted"
    />

    <!-- 수정지시 다이얼로그 (2026-07-17) -->
    <el-dialog
      v-model="showRevisionDialog"
      :title="editingRevision ? '수정지시 수정' : '수정지시 추가'"
      width="520px"
    >
      <el-form label-width="90px">
        <el-form-item label="수정지시일">
          <el-date-picker
            v-model="revisionForm.revision_date"
            type="date"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="구분">
          <el-input v-model="revisionForm.title" placeholder="예: 가봉, 직봉, QC (선택)" />
        </el-form-item>
        <el-form-item label="수정 항목">
          <el-input
            v-model="revisionForm.content"
            type="textarea"
            :rows="8"
            placeholder="한 줄에 한 항목씩 입력 — 출력 시 번호가 자동 부여됩니다"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showRevisionDialog = false">취소</el-button>
        <el-button type="primary" @click="saveRevision">저장</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Edit, Check, Close, Plus, Delete, Download, ArrowDown, Promotion, Document } from '@element-plus/icons-vue'
import axios from '@/api/axios'
import ApprovalSubmitDialog from '@/components/approval/ApprovalSubmitDialog.vue'

// 탭 임베드 모드 (2026-07-19): MR 상세의 '생산지시서' 탭에서 컴포넌트로 재사용.
// workInstructionId를 넘기면 라우트 대신 그 id로 동작한다.
const props = defineProps({
  workInstructionId: { type: [Number, String], default: null },
})
const route = useRoute()
const wiId = () => props.workInstructionId || route.params.id

// State
const workInstruction = reactive({})
const isEditing = ref(false)
const loading = ref(false)
const activeTab = ref('specs')

const formData = reactive({
  mix_ratio: '',
  process_method: '',
  total_quantity: 0,
  memo: '',
  shipment_date: null,
  packing_method: '',
  precautions: '',
  manufacturer: null,
  product_code: '',
  base_size: '',
  image: null
})

// 자재 구분 (2026-07-29 fit-gap ②) — 지시서 표기 블록
const categoryOptions = [
  { value: 'COLOR_BLOCK', label: '배색' },
  { value: 'LINING', label: '안감' },
  { value: 'INTERLINING', label: '심지' },
  { value: 'THREAD', label: '봉사' },
  { value: 'ACCESSORY', label: '부자재' },
  { value: 'PACKING', label: '포장' },
  { value: 'OTHER', label: '기타' }
]

// 결재 (2026-07-29 fit-gap ③)
const showApprovalDialog = ref(false)
const onApprovalSubmitted = async () => {
  ElMessage.success('결재를 상신했습니다.')
  await loadData()
}

const precautionLines = computed(() =>
  (workInstruction.precautions || '').split('\n').map(l => l.trim()).filter(Boolean)
)

// 참조 목록 (2026-07-29) — 생산처 셀렉트 + 자재 거래처 드롭다운
const manufacturers = ref([])
const suppliers = ref([])
const loadRefLists = async () => {
  try {
    const [mfRes, spRes] = await Promise.all([
      axios.get('/production/manufacturers/', { params: { page_size: 500 } }),
      axios.get('/suppliers/', { params: { page_size: 1000 } })
    ])
    manufacturers.value = mfRes.data.results || mfRes.data || []
    suppliers.value = spRes.data.results || spRes.data || []
  } catch (e) {
    console.error('참조 목록 로드 실패:', e)
  }
}

const specs = ref([])
const quantities = ref([])
const materials = ref([])
const revisions = ref([])
// 발행 이력 (2026-07-29)
const versions = ref([])
const showIssueDialog = ref(false)
const issuing = ref(false)
const issueForm = reactive({ paper: 'A4', note: '' })
const nextVersionNo = computed(() => (versions.value[0]?.version_no || 0) + 1)
const showRevisionDialog = ref(false)
const editingRevision = ref(null)
const revisionForm = reactive({
  revision_date: '',
  title: '',
  content: ''
})

// Computed properties
const mainRequestNumber = computed(() => {
  return workInstruction.main_request_number || '-'
})

const designInfo = computed(() => {
  const num = workInstruction.design_number || ''
  const name = workInstruction.design_name || ''
  return [num, name].filter(Boolean).join(' - ') || '-'
})

const brandInfo = computed(() => {
  return workInstruction.brand_name || '-'
})

const manufacturerInfo = computed(() => {
  return workInstruction.manufacturer_name || '-'
})

const uniqueColors = computed(() => {
  const colors = new Set(quantities.value.map(q => q.color))
  return Array.from(colors).sort()
})

const uniqueSizes = computed(() => {
  const sizes = new Set(quantities.value.map(q => q.size))
  return Array.from(sizes).sort()
})

const quantityTableData = computed(() => {
  const data = []
  uniqueSizes.value.forEach(size => {
    const row = { size, total: 0 }
    let sizeTotal = 0
    uniqueColors.value.forEach(color => {
      const qty = quantities.value.find(
        q => q.size === size && q.color === color
      )
      const value = qty ? qty.quantity : 0
      row[color] = value
      sizeTotal += value
    })
    row.total = sizeTotal
    data.push(row)
  })
  return data
})

// Methods
const getStatusLabel = (status) => {
  const map = {
    DRAFT: '작성중',
    CONFIRMED: '확정',
    SENT: '발송',
    IN_PRODUCTION: '생산중',
    COMPLETED: '완료'
  }
  return map[status] || status
}

const getStatusType = (status) => {
  const map = {
    DRAFT: 'info',
    CONFIRMED: 'primary',
    SENT: 'warning',
    IN_PRODUCTION: 'success',
    COMPLETED: ''
  }
  return map[status] || 'info'
}

const getMaterialTypeLabel = (type) => {
  return type === 'RAW' ? '원자재' : type === 'ACCESSORY' ? '부자재' : type
}

const getMaterialTypeTag = (type) => {
  return type === 'RAW' ? 'success' : type === 'ACCESSORY' ? 'primary' : 'info'
}

const loadData = async () => {
  const id = wiId()
  if (!id || id === 'undefined') return  // 방어: id 미확정 상태에서 undefined 요청 방지
  try {
    loading.value = true

    // Load main work instruction
    const wiRes = await axios.get(`/production/work-instructions/${id}/`)
    Object.assign(workInstruction, wiRes.data)
    initializeFormData()

    // Load specs
    const specsRes = await axios.get(
      `/production/work-instruction-specs/?work_instruction=${id}`
    )
    specs.value = specsRes.data.results || specsRes.data

    // Load quantities
    const qtRes = await axios.get(
      `/production/work-instruction-quantities/?work_instruction=${id}`
    )
    quantities.value = qtRes.data.results || qtRes.data

    // Load materials
    const matRes = await axios.get(
      `/production/work-instruction-materials/?work_instruction=${id}`
    )
    materials.value = matRes.data.results || matRes.data

    // Load revisions (수정이력, 2026-07-17)
    const revRes = await axios.get(
      `/production/work-instruction-revisions/?work_instruction=${id}`
    )
    revisions.value = revRes.data.results || revRes.data

    // Load versions (발행 이력, 2026-07-29)
    const verRes = await axios.get(
      `/production/work-instruction-versions/?work_instruction=${id}`
    )
    versions.value = verRes.data.results || verRes.data

    fetchMrDiff() // MR↔WI 불일치 감지 (실패해도 화면은 정상 — await 안 함)
    fetchWiImages()
  } catch (error) {
    ElMessage.error('생산지시서 로드 실패: ' + (error.response?.data?.detail || error.message))
    console.error(error)
  } finally {
    loading.value = false
  }
}

// ── MR↔WI 불일치 감지 + 동기화 (2026-08-01) ──
// 메인오더 수량·자재가 바뀐 뒤 생산지시서가 낡은 채로 공장 발행되는
// 커뮤니케이션 사고 방지. 서버 diff-main-request(저장 0 파생) 사용.
const mrDiff = ref(null)
const syncing = ref(false)

const mrDiffSummary = computed(() => {
  const d = mrDiff.value
  if (!d || d.match) return ''
  const parts = []
  if (!d.quantity.match) {
    parts.push(`수량 ${d.quantity.diff_count}건 (지시 ${Number(d.quantity.mr_total).toLocaleString()} / 문서 ${Number(d.quantity.wi_total).toLocaleString()})`)
  }
  if (!d.materials.match) {
    const names = d.materials.diff_names.join(', ')
    parts.push(`자재 ${d.materials.diff_count}건${names ? ` (${names}${d.materials.diff_count > d.materials.diff_names.length ? ' 외' : ''})` : ''}`)
  }
  if (d.specs && !d.specs.match) {
    parts.push(`완성치수 ${d.specs.diff_count}건`)
  }
  return parts.join(' · ')
})

const fetchMrDiff = async () => {
  try {
    const res = await axios.get(`/production/work-instructions/${wiId()}/diff-main-request/`)
    mrDiff.value = res.data
  } catch (e) { mrDiff.value = null } // 구서버 등 미지원이면 조용히 숨김
}

const syncFromMainRequest = async () => {
  syncing.value = true
  try {
    const res = await axios.post(`/production/work-instructions/${wiId()}/materials/from-main-request/?mode=sync`)
    ElMessage.success(res.data.message || '동기화했습니다.')
    await loadData()
  } catch (e) {
    ElMessage.error('동기화에 실패했습니다.')
  } finally {
    syncing.value = false
  }
}

// 결재 상신도 같은 가드 경유 (낡은 문서로 결재 올라가는 것 방지)
const openApprovalDialog = async () => {
  if (!await confirmIfStale('결재 상신')) return
  showApprovalDialog.value = true
}

// 발행·PDF·결재 직전 가드 — 불일치 상태로 나가는 문서는 반드시 확인을 거침
const confirmIfStale = async (actionLabel) => {
  if (!mrDiff.value || mrDiff.value.match) return true
  try {
    await ElMessageBox.confirm(
      `메인오더와 불일치한 상태입니다 (${mrDiffSummary.value}). 이대로 ${actionLabel}하시겠습니까?`,
      '불일치 경고',
      { confirmButtonText: '이대로 진행', cancelButtonText: '취소', type: 'warning' }
    )
    return true
  } catch { return false }
}

const initializeFormData = () => {
  formData.mix_ratio = workInstruction.mix_ratio || ''
  formData.process_method = workInstruction.process_method || ''
  formData.total_quantity = workInstruction.total_quantity || 0
  formData.memo = workInstruction.memo || ''
  formData.shipment_date = workInstruction.shipment_date || null
  formData.packing_method = workInstruction.packing_method || ''
  formData.precautions = workInstruction.precautions || ''
  formData.manufacturer = workInstruction.manufacturer || null
  formData.product_code = workInstruction.product_code || ''
  formData.base_size = workInstruction.base_size || ''
  formData.image = null
}

const enterEditMode = () => {
  isEditing.value = true
  initializeFormData()
  resetDeleted()
}

const resetDeleted = () => {
  deletedSpecIds.value = []
  deletedQuantityIds.value = []
  deletedMaterialIds.value = []
}

const cancelEdit = () => {
  isEditing.value = false
  initializeFormData()
  resetDeleted()
  loadData() // 편집 중 화면에서만 지운 행 복원 (2026-08-01)
}

// ── 스케치 다중 이미지 (2026-08-01) — 즉시 업로드/삭제 ──
const wiImages = ref([])
const newImageCaption = ref('')

const fetchWiImages = async () => {
  try {
    const res = await axios.get('/production/work-instruction-images/', {
      params: { work_instruction: wiId() } })
    wiImages.value = res.data.results || res.data || []
  } catch (e) { console.error('스케치 이미지 조회 실패:', e) }
}

const uploadWiImage = async (file) => {
  const fd = new FormData()
  fd.append('work_instruction', wiId())
  fd.append('image', file.raw)
  fd.append('caption', newImageCaption.value || '')
  fd.append('sort_order', wiImages.value.length)
  try {
    await axios.post('/production/work-instruction-images/', fd)
    newImageCaption.value = ''
    await fetchWiImages()
    ElMessage.success('스케치 이미지를 추가했습니다.')
  } catch (e) {
    ElMessage.error('이미지 업로드 실패')
  }
}

const deleteWiImage = async (im) => {
  try {
    await axios.delete(`/production/work-instruction-images/${im.id}/`)
    await fetchWiImages()
  } catch (e) { ElMessage.error('이미지 삭제 실패') }
}

const handleSave = async () => {
  try {
    loading.value = true
    const id = wiId()

    // Prepare data
    const updateData = {
      mix_ratio: formData.mix_ratio,
      process_method: formData.process_method,
      total_quantity: formData.total_quantity,
      memo: formData.memo,
      shipment_date: formData.shipment_date || null,
      packing_method: formData.packing_method,
      precautions: formData.precautions,
      manufacturer: formData.manufacturer || null,
      product_code: formData.product_code,
      base_size: formData.base_size
    }

    if (formData.image) {
      const formDataWithImage = new FormData()
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === null || updateData[key] === undefined) return  // "null" 문자열 방지
        formDataWithImage.append(key, updateData[key])
      })
      formDataWithImage.append('image', formData.image)
      await axios.patch(`/production/work-instructions/${id}/`, formDataWithImage)
    } else {
      await axios.patch(`/production/work-instructions/${id}/`, updateData)
    }

    // 삭제된 행 서버 반영 (2026-08-01)
    for (const sid of deletedSpecIds.value) {
      await axios.delete(`/production/work-instruction-specs/${sid}/`)
    }
    for (const qid of deletedQuantityIds.value) {
      await axios.delete(`/production/work-instruction-quantities/${qid}/`)
    }
    for (const mid of deletedMaterialIds.value) {
      await axios.delete(`/production/work-instruction-materials/${mid}/`)
    }
    deletedSpecIds.value = []
    deletedQuantityIds.value = []
    deletedMaterialIds.value = []

    // Save specs
    for (const spec of specs.value) {
      if (spec.id) {
        await axios.patch(`/production/work-instruction-specs/${spec.id}/`, spec)
      } else {
        await axios.post('/production/work-instruction-specs/', {
          ...spec,
          work_instruction: id
        })
      }
    }

    // Save quantities
    for (const qty of quantities.value) {
      if (qty.id) {
        await axios.patch(`/production/work-instruction-quantities/${qty.id}/`, qty)
      } else {
        await axios.post('/production/work-instruction-quantities/', {
          ...qty,
          work_instruction: id
        })
      }
    }

    // Save materials
    for (const mat of materials.value) {
      if (mat.id) {
        await axios.patch(`/production/work-instruction-materials/${mat.id}/`, mat)
      } else {
        await axios.post('/production/work-instruction-materials/', {
          ...mat,
          work_instruction: id
        })
      }
    }

    ElMessage.success('생산지시서가 저장되었습니다')
    isEditing.value = false
    await loadData()
  } catch (error) {
    ElMessage.error('저장 실패: ' + (error.response?.data?.detail || error.message))
    console.error(error)
  } finally {
    loading.value = false
  }
}

const addNewSpec = () => {
  specs.value.push({
    part: '',
    size: '',
    measurement: ''
  })
}

// 삭제 추적 (2026-08-01, 사이먼: "수정에서 삭제해도 삭제 안 됨") —
// splice는 화면에서만 지움. 저장 시 서버 DELETE를 보내야 실제 삭제된다
const deletedSpecIds = ref([])
const deletedQuantityIds = ref([])
const deletedMaterialIds = ref([])

const removeSpec = (index) => {
  const r = specs.value[index]
  if (r?.id) deletedSpecIds.value.push(r.id)
  specs.value.splice(index, 1)
}

const addNewQuantity = () => {
  quantities.value.push({
    color: '',
    size: '',
    quantity: 0,
    spare_quantity: 0
  })
}

// 여유분 조회 (색상×사이즈) — 표시용 "1,140+3" (2026-07-29)
const spareAt = (color, size) => {
  const q = quantities.value.find(x => x.color === color && x.size === size)
  return q?.spare_quantity || 0
}

const removeQuantity = (index) => {
  const r = quantities.value[index]
  if (r?.id) deletedQuantityIds.value.push(r.id)
  quantities.value.splice(index, 1)
}

const updateQuantityTotal = () => {
  // Trigger reactive update
  quantities.value = [...quantities.value]
}

const addNewMaterial = () => {
  materials.value.push({
    material_type: 'RAW',
    category: 'OTHER',
    material_name: '',
    usage_part: '',
    required_quantity: 0,
    supplier: null
  })
}

const removeMaterial = (index) => {
  const r = materials.value[index]
  if (r?.id) deletedMaterialIds.value.push(r.id)
  materials.value.splice(index, 1)
}

// 출력은 PDF 단일 경로 (2026-07-30 사이먼) — 엑셀 출력·브라우저 인쇄 제거.
// 서버 export-excel API는 유지(내부/타 화면 재사용 여지), 이 화면에선 미노출.

// ── 생산지시서 PDF 출력 (2026-07-29) — 새 탭에서 미리보기 ──
const downloadInstructionPdf = async (paper = 'A4') => {
  if (!await confirmIfStale('PDF 출력')) return
  try {
    const id = wiId()
    const res = await axios.get(`/production/work-instructions/${id}/export-pdf/`, {
      params: { paper },
      responseType: 'blob'
    })
    const url = URL.createObjectURL(res.data)
    window.open(url, '_blank')
  } catch (error) {
    ElMessage.error('PDF 출력 실패')
    console.error(error)
  }
}

// ── 공장 발행 — 버전 스냅샷 (2026-07-29) ──
const openIssueDialog = async () => {
  if (!await confirmIfStale('공장 발행')) return
  issueForm.paper = 'A3'  // 공장 발송이 발행의 기본 목적 — 스와치 부착란 포함 A3
  issueForm.note = ''
  showIssueDialog.value = true
}

const submitIssue = async () => {
  issuing.value = true
  try {
    const res = await axios.post(`/production/work-instructions/${wiId()}/issue/`, {
      paper: issueForm.paper,
      note: issueForm.note
    })
    ElMessage.success(res.data.message || '발행되었습니다')
    showIssueDialog.value = false
    activeTab.value = 'versions'
    await loadData()
    // 발행 직후 발행본 바로 내려받기 — 공장 전달 흐름 단축
    if (res.data.version?.id) {
      await downloadVersion(res.data.version)
    }
  } catch (error) {
    ElMessage.error('발행 실패: ' + (error.response?.data?.error || error.message))
  } finally {
    issuing.value = false
  }
}

// 공장 접수확인 (2026-07-29) — RECEIVED 도장 대응
const acknowledgeVersion = async (row) => {
  try {
    const { value } = await ElMessageBox.prompt(
      `Rev.${row.version_no} 발행본을 공장이 접수했습니까? 접수자명을 입력하세요.`,
      '공장 접수확인',
      { confirmButtonText: '접수확인', cancelButtonText: '취소', inputPlaceholder: '예: MHT 권현정' }
    )
    if (!value || !value.trim()) {
      ElMessage.warning('접수자명을 입력하세요.')
      return
    }
    await axios.post(`/production/work-instruction-versions/${row.id}/acknowledge/`, { name: value.trim() })
    ElMessage.success('접수확인 기록됨')
    await loadData()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('접수확인 실패: ' + (error.response?.data?.error || error.message))
    }
  }
}

const downloadVersion = async (row) => {
  try {
    const res = await axios.get(
      `/production/work-instruction-versions/${row.id}/download/`,
      { responseType: 'blob' }
    )
    // 확장자는 보존 파일 기준 (발행본=PDF)
    const ext = (row.file_url || '').split('.').pop()?.toLowerCase() === 'xlsx' ? 'xlsx' : 'pdf'
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `생산지시서_${workInstruction.instruction_number}_Rev${row.version_no}.${ext}`
    a.click()
    URL.revokeObjectURL(url)
  } catch (error) {
    ElMessage.error('발행본 다운로드 실패')
    console.error(error)
  }
}

// ── 수정이력 (2026-07-17) ──
const revisionLines = (rev) => {
  return (rev.content || '').split('\n').map((l) => l.trim()).filter(Boolean)
}

const openRevisionDialog = (rev = null) => {
  editingRevision.value = rev
  revisionForm.revision_date = rev?.revision_date || new Date().toISOString().slice(0, 10)
  revisionForm.title = rev?.title || ''
  revisionForm.content = rev?.content || ''
  showRevisionDialog.value = true
}

const saveRevision = async () => {
  if (!revisionForm.revision_date || !revisionForm.content.trim()) {
    ElMessage.warning('수정지시일과 수정 항목을 입력하세요.')
    return
  }
  try {
    const payload = {
      work_instruction: wiId(),
      revision_date: revisionForm.revision_date,
      title: revisionForm.title,
      content: revisionForm.content
    }
    if (editingRevision.value) {
      await axios.patch(
        `/production/work-instruction-revisions/${editingRevision.value.id}/`,
        payload
      )
    } else {
      await axios.post('/production/work-instruction-revisions/', payload)
    }
    ElMessage.success('수정지시가 저장되었습니다')
    showRevisionDialog.value = false
    await loadData()
  } catch (error) {
    ElMessage.error('저장 실패: ' + (error.response?.data?.detail || error.message))
  }
}

const deleteRevision = async (rev) => {
  try {
    await ElMessageBox.confirm('이 수정지시를 삭제하시겠습니까?', '삭제', { type: 'warning' })
    await axios.delete(`/production/work-instruction-revisions/${rev.id}/`)
    ElMessage.success('삭제되었습니다')
    await loadData()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('삭제 실패')
    }
  }
}

// 메인오더 자재현황 → 자재표 가져오기 (2026-07-19, 이중 입력 제거 — 생성 시 자동 승계의 수동 재실행)
const importingMaterials = ref(false)
const importMaterialsFromMainRequest = async () => {
  importingMaterials.value = true
  try {
    const res = await axios.post(`/production/work-instructions/${wiId()}/materials/from-main-request/`)
    ElMessage.success(res.data.message || '가져왔습니다.')
    await loadData()
  } catch (e) {
    console.error('자재 가져오기 실패:', e)
    ElMessage.error(e.response?.data?.error || '자재 가져오기에 실패했습니다.')
  } finally {
    importingMaterials.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadData()
  loadRefLists()
})
watch(() => props.workInstructionId, (v, old) => {
  if (v && v !== old) loadData()
})
</script>

<style scoped>
/* 스케치 갤러리 (2026-08-01) */
.sketch-gallery { display: flex; flex-wrap: wrap; gap: 8px; align-items: flex-start; }
.sk-item { margin: 0; text-align: center; }
.sk-thumb { width: 120px; height: 120px; border: 1px solid #ebeef5; border-radius: 4px; }
.sk-item figcaption { font-size: 12px; color: #606266; margin-top: 2px; }
.sk-uploader { display: flex; gap: 4px; align-items: center; }

.work-instruction-detail {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.header-section {
  margin-bottom: 20px;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  background: white;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.title-group {
  display: flex;
  align-items: center;
  gap: 15px;
}

.title-group h1 {
  margin: 0;
  font-size: 24px;
  color: #333;
}

.status-tag {
  padding: 8px 12px;
  font-size: 14px;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.mr-diff-alert { margin-bottom: 12px; }

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
}

.summary-card {
  cursor: pointer;
  transition: all 0.3s ease;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.card-header {
  padding: 0;
  font-weight: bold;
  color: #333;
  font-size: 14px;
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
}

.info-row .label {
  font-weight: 600;
  color: #666;
  font-size: 13px;
  min-width: 70px;
}

.info-row .value {
  color: #333;
  font-size: 13px;
  flex: 1;
  text-align: right;
}

.info-row .font-bold {
  font-weight: bold;
  color: #409eff;
}

.basic-info-card {
  margin-bottom: 20px;
}

.tabs-card {
  margin-bottom: 20px;
}

.tab-content {
  padding: 20px 0;
}

.tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.tab-header h3 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.empty-state {
  padding: 40px 0;
  text-align: center;
}

.image-upload {
  width: 100%;
}

.image-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #ddd;
  border-radius: 4px;
  padding: 20px;
}

.text-muted {
  color: #999;
}

.pivot-container {
  overflow-x: auto;
  margin-bottom: 20px;
}

.revision-card :deep(.el-card__body) {
  padding: 10px 14px;
}

.revision-list {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  color: #333;
}

.revision-list li {
  padding: 1px 0;
}

.revision-actions {
  margin-top: 6px;
  text-align: right;
}

.version-badge {
  margin-left: 6px;
}

.ack-info {
  font-size: 11px;
  color: #909399;
  margin-top: 2px;
}

.precaution-list {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  color: #333;
}

.spare-plus {
  color: #E6A23C;
  font-size: 12px;
  margin-left: 1px;
}

.paper-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

/* Print styles */
@media print {
  .header-section .action-buttons,
  .tab-header .el-button {
    display: none;
  }

  .work-instruction-detail {
    padding: 0;
    background-color: white;
  }
}
</style>
